import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Settings, User, Layers, Save, Image, Plus, Trash2, Bell, BellOff, Edit2, Globe } from 'lucide-react';
import {
  requestNotificationPermission,
  getCurrentPermission,
  isNotificationSupported,
  getNotificationSettings,
  saveNotificationSettingsLocal,
  sendBrowserNotification,
} from '../services/NotificationService';

const API = process.env.REACT_APP_BACKEND_URL;

const GROUP_TYPES = [
  { value: 'indoor', label: 'Indoor' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'greenhouse', label: 'Gewächshaus' },
  { value: 'other', label: 'Sonstiges' },
];

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const [tab, setTab] = useState('profile');

  const [profile, setProfile] = useState({ name: user?.name || '', bio: user?.bio || '', avatar: user?.avatar || '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', group_type: 'indoor', description: '' });
  const [savingGroup, setSavingGroup] = useState(false);

  const [breeders, setBreeders] = useState([]);
  const [breedersLoaded, setBreedersLoaded] = useState(false);
  const [editingBreeder, setEditingBreeder] = useState(null);
  const [breederForm, setBreederForm] = useState({ name: '', country: '', website: '', description: '' });
  const [savingBreeder, setSavingBreeder] = useState(false);

  // Notification settings
  const [notifPerm, setNotifPerm] = useState(getCurrentPermission());
  const [notifSettings, setNotifSettings] = useState({
    enabled: false, reminder_time: '08:00', watering_interval_days: 2,
    ...getNotificationSettings()
  });
  const [savingNotif, setSavingNotif] = useState(false);
  const [notifMsg, setNotifMsg] = useState('');

  const loadGroups = async () => {
    if (groupsLoaded) return;
    try {
      const { data } = await axios.get(`${API}/api/groups`);
      setGroups(Array.isArray(data) ? data : []);
      setGroupsLoaded(true);
    } catch {
      setGroups([]);
      setGroupsLoaded(true);
    }
  };

  const loadBreeders = async () => {
    if (breedersLoaded) return;
    try {
      const { data } = await axios.get(`${API}/api/breeders`);
      setBreeders(Array.isArray(data) ? data : []);
      setBreedersLoaded(true);
    } catch {
      setBreeders([]);
      setBreedersLoaded(true);
    }
  };

  const saveBreeder = async (e) => {
    e.preventDefault();
    if (!breederForm.name.trim()) return;
    setSavingBreeder(true);
    try {
      const payload = {
        name: breederForm.name.trim(),
        country: breederForm.country.trim() || null,
        website: breederForm.website.trim() || null,
        description: breederForm.description.trim() || null,
      };
      if (editingBreeder) {
        const { data } = await axios.put(`${API}/api/breeders/${editingBreeder.id}`, payload);
        setBreeders(prev => prev.map(b => b.id === data.id ? data : b));
      } else {
        const { data } = await axios.post(`${API}/api/breeders`, payload);
        setBreeders(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setEditingBreeder(null);
      setBreederForm({ name: '', country: '', website: '', description: '' });
    } catch {}
    setSavingBreeder(false);
  };

  const deleteBreeder = async (id) => {
    if (!window.confirm('Breeder löschen?')) return;
    try {
      await axios.delete(`${API}/api/breeders/${id}`);
      setBreeders(prev => prev.filter(b => b.id !== id));
    } catch {}
  };

  const startEditBreeder = (b) => {
    setEditingBreeder(b);
    setBreederForm({
      name: b.name || '',
      country: b.country || '',
      website: b.website || '',
      description: b.description || '',
    });
  };

  const cancelEditBreeder = () => {
    setEditingBreeder(null);
    setBreederForm({ name: '', country: '', website: '', description: '' });
  };

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'groups') loadGroups();
    if (t === 'breeders') loadBreeders();
  };

  const handleRequestNotifPermission = async () => {
    const result = await requestNotificationPermission();
    setNotifPerm(result);
    if (result === 'granted') {
      setNotifSettings(s => ({ ...s, enabled: true }));
      sendBrowserNotification('GrowManager', 'Benachrichtigungen erfolgreich aktiviert!');
    }
  };

  const handleSaveNotifications = async (e) => {
    e.preventDefault();
    setSavingNotif(true);
    saveNotificationSettingsLocal(notifSettings);
    try {
      await axios.put(`${API}/api/auth/notification-settings`, notifSettings);
    } catch {}
    setSavingNotif(false);
    setNotifMsg('Einstellungen gespeichert!');
    setTimeout(() => setNotifMsg(''), 3000);
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setProfile(p => ({ ...p, avatar: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMsg('');
    try {
      const { data } = await axios.put(`${API}/api/auth/profile`, profile);
      updateUser(data);
      setProfileMsg('Profil gespeichert!');
      setTimeout(() => setProfileMsg(''), 3000);
    } catch {}
    setSavingProfile(false);
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim()) return;
    setSavingGroup(true);
    try {
      const { data } = await axios.post(`${API}/api/groups`, newGroup);
      setGroups(prev => [...prev, data]);
      setNewGroup({ name: '', group_type: 'indoor', description: '' });
    } catch {}
    setSavingGroup(false);
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Gruppe löschen?')) return;
    try {
      await axios.delete(`${API}/api/groups/${id}`);
      setGroups(prev => prev.filter(g => g.id !== id));
    } catch {}
  };

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-emerald-50">Einstellungen</h1>
        <p className="text-emerald-100/50 text-sm mt-0.5">Profil und Konfiguration</p>
      </div>

      {/* Tabs */}
      <div className="flex rounded-lg p-1 w-fit" style={{ background: '#0A120D', border: '1px solid #1B2A21' }}>
        {[
          { key: 'profile', label: 'Profil', icon: User },
          { key: 'groups', label: 'Grow-Gruppen', icon: Layers },
          { key: 'breeders', label: 'Breeder', icon: Globe },
          { key: 'notifications', label: 'Benachrichtigungen', icon: Bell },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => handleTabChange(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === key ? 'bg-emerald-500 text-white' : 'text-emerald-100/50 hover:text-emerald-100'}`}
            data-testid={`settings-tab-${key}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {tab === 'profile' && (
        <div className="card-grow p-5 max-w-md" data-testid="profile-tab">
          <form onSubmit={handleSaveProfile} className="space-y-4">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
                style={{ background: '#111C15', border: '2px solid #1B2A21' }}>
                {profile.avatar ? (
                  <img src={profile.avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-emerald-400">
                    <User size={28} />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ cursor: 'pointer', fontSize: 13, color: '#4ade80' }}>
                  📷 Foto aufnehmen
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handleAvatarUpload}
                  />
                </label>
                <label style={{ cursor: 'pointer', fontSize: 13, color: '#60a5fa' }}>
                  🖼️ Aus Galerie
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleAvatarUpload}
                  />
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Name</label>
              <input className="input-grow" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} data-testid="profile-name-input" />
            </div>
            <div>
              <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">E-Mail</label>
              <input className="input-grow" value={user?.email || ''} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }} />
            </div>
            <div>
              <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Bio</label>
              <textarea className="input-grow" rows={3} placeholder="Kurze Beschreibung..." value={profile.bio} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} data-testid="profile-bio-input" />
            </div>

            {profileMsg && <p className="text-emerald-400 text-sm">{profileMsg}</p>}

            <button type="submit" disabled={savingProfile} className="btn-primary flex items-center gap-2" data-testid="save-profile-btn">
              <Save size={14} /> {savingProfile ? 'Speichern...' : 'Profil speichern'}
            </button>
          </form>
        </div>
      )}

      {/* Groups Tab */}
      {tab === 'groups' && (
        <div className="space-y-4 max-w-lg" data-testid="groups-tab">
          {/* Create group */}
          <div className="card-grow p-4">
            <h3 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2">
              <Plus size={16} className="text-emerald-400" /> Neue Gruppe
            </h3>
            <form onSubmit={handleCreateGroup} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Name</label>
                  <input className="input-grow" placeholder="z.B. Indoor Tent #1" value={newGroup.name} onChange={e => setNewGroup(n => ({ ...n, name: e.target.value }))} data-testid="group-name-input" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Typ</label>
                  <select className="input-grow" value={newGroup.group_type} onChange={e => setNewGroup(n => ({ ...n, group_type: e.target.value }))} data-testid="group-type-select">
                    {GROUP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" disabled={savingGroup} className="btn-primary flex items-center gap-2 text-sm" data-testid="create-group-btn">
                <Plus size={14} /> {savingGroup ? 'Anlegen...' : 'Gruppe anlegen'}
              </button>
            </form>
          </div>

          {/* Groups list */}
          <div className="card-grow p-4">
            <h3 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2">
              <Layers size={16} className="text-emerald-400" /> Meine Gruppen ({groups.length})
            </h3>
            {groups.length === 0 ? (
              <p className="text-sm text-emerald-100/30 text-center py-3">Noch keine Gruppen</p>
            ) : (
              <div className="space-y-2">
                {groups.map(g => (
                  <div key={g.id} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: '#050A07', border: '1px solid #1B2A21' }} data-testid={`group-item-${g.id}`}>
                    <div>
                      <p className="text-sm font-medium text-emerald-100">{g.name}</p>
                      <p className="text-xs text-emerald-100/40">{GROUP_TYPES.find(t => t.value === g.group_type)?.label || g.group_type}</p>
                    </div>
                    <button onClick={() => handleDeleteGroup(g.id)} className="p-1.5 rounded text-emerald-100/30 hover:text-red-400 transition-colors" data-testid={`delete-group-${g.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Breeders Tab */}
      {tab === 'breeders' && (
        <div className="space-y-4 max-w-lg" data-testid="breeders-tab">
          {/* Create / Edit breeder form */}
          <div className="card-grow p-4">
            <h3 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2">
              {editingBreeder ? <Edit2 size={16} className="text-emerald-400" /> : <Plus size={16} className="text-emerald-400" />}
              {editingBreeder ? `Breeder bearbeiten: ${editingBreeder.name}` : 'Neuer Breeder'}
            </h3>
            <form onSubmit={saveBreeder} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Name *</label>
                  <input className="input-grow" placeholder="z.B. DNA Genetics" value={breederForm.name}
                    onChange={e => setBreederForm(f => ({ ...f, name: e.target.value }))}
                    data-testid="breeder-name-input" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Land</label>
                  <input className="input-grow" placeholder="z.B. USA" value={breederForm.country}
                    onChange={e => setBreederForm(f => ({ ...f, country: e.target.value }))}
                    data-testid="breeder-country-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Website</label>
                <input className="input-grow" placeholder="https://example.com" value={breederForm.website}
                  onChange={e => setBreederForm(f => ({ ...f, website: e.target.value }))}
                  data-testid="breeder-website-input" />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Beschreibung</label>
                <textarea className="input-grow" rows={2} placeholder="Kurze Beschreibung..." value={breederForm.description}
                  onChange={e => setBreederForm(f => ({ ...f, description: e.target.value }))}
                  data-testid="breeder-desc-input" />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={savingBreeder} className="btn-primary flex items-center gap-2 text-sm" data-testid="save-breeder-btn">
                  <Save size={14} /> {savingBreeder ? 'Speichern...' : editingBreeder ? 'Aktualisieren' : 'Breeder anlegen'}
                </button>
                {editingBreeder && (
                  <button type="button" onClick={cancelEditBreeder} className="btn-secondary text-sm" data-testid="cancel-edit-breeder-btn">
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Breeder list */}
          <div className="card-grow p-4">
            <h3 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2">
              <Globe size={16} className="text-emerald-400" /> Meine Breeder ({breeders.length})
            </h3>
            {breeders.length === 0 ? (
              <p className="text-sm text-emerald-100/30 text-center py-3">Noch keine Breeder</p>
            ) : (
              <div className="space-y-2">
                {breeders.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: '#050A07', border: '1px solid #1B2A21' }} data-testid={`breeder-item-${b.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-emerald-100">{b.name}</p>
                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        {b.country && <span className="text-xs text-emerald-100/40">{b.country}</span>}
                        {b.website && (
                          <a href={b.website} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-emerald-400 hover:text-emerald-300 underline truncate max-w-[160px]"
                            onClick={e => e.stopPropagation()}>
                            {b.website.replace(/^https?:\/\//, '')}
                          </a>
                        )}
                      </div>
                      {b.description && <p className="text-xs text-emerald-100/40 mt-0.5 line-clamp-1">{b.description}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0 ml-2">
                      <button onClick={() => startEditBreeder(b)} className="p-1.5 rounded text-emerald-100/30 hover:text-emerald-400 transition-colors" data-testid={`edit-breeder-${b.id}`}>
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => deleteBreeder(b.id)} className="p-1.5 rounded text-emerald-100/30 hover:text-red-400 transition-colors" data-testid={`delete-breeder-${b.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {tab === 'notifications' && (
        <div className="card-grow p-5 max-w-md" data-testid="notifications-tab">
          <form onSubmit={handleSaveNotifications} className="space-y-5">
            {/* Permission Status */}
            <div className="flex items-start gap-3 p-3 rounded-lg" style={{ background: '#111C15', border: '1px solid #1B2A21' }}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${notifPerm === 'granted' ? 'text-emerald-400' : 'text-yellow-400'}`}
                style={{ background: notifPerm === 'granted' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)' }}>
                {notifPerm === 'granted' ? <Bell size={18} /> : <BellOff size={18} />}
              </div>
              <div className="flex-1">
                {notifPerm === 'granted' ? (
                  <p className="text-sm text-emerald-400 font-medium">Benachrichtigungen erlaubt</p>
                ) : notifPerm === 'denied' ? (
                  <div>
                    <p className="text-sm text-red-400 font-medium">Benachrichtigungen blockiert</p>
                    <p className="text-xs text-emerald-100/40 mt-0.5">Bitte in den Browser-Einstellungen entsperren</p>
                  </div>
                ) : notifPerm === 'not-supported' ? (
                  <p className="text-sm text-emerald-100/50">Browser unterstützt keine Benachrichtigungen</p>
                ) : (
                  <div>
                    <p className="text-sm text-yellow-400 font-medium">Berechtigung nicht erteilt</p>
                    <button type="button" onClick={handleRequestNotifPermission}
                      className="btn-primary text-sm mt-2 flex items-center gap-2" data-testid="request-permission-btn">
                      <Bell size={14} /> Benachrichtigungen aktivieren
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Enable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-100">Erinnerungen aktiv</p>
                <p className="text-xs text-emerald-100/40">Täglich zur gewählten Zeit</p>
              </div>
              <button type="button"
                onClick={() => setNotifSettings(s => ({ ...s, enabled: !s.enabled }))}
                disabled={notifPerm !== 'granted'}
                className={`w-12 h-6 rounded-full transition-colors relative ${notifSettings.enabled && notifPerm === 'granted' ? 'bg-emerald-500' : 'bg-[#1B2A21]'}`}
                data-testid="notifications-toggle">
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${notifSettings.enabled && notifPerm === 'granted' ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Reminder Time */}
            <div>
              <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Erinnerungszeit</label>
              <input type="time" className="input-grow" value={notifSettings.reminder_time}
                onChange={e => setNotifSettings(s => ({ ...s, reminder_time: e.target.value }))}
                data-testid="reminder-time-input" />
            </div>

            {/* Watering Interval */}
            <div>
              <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">
                Gieß-Erinnerung nach (Tage): <span className="text-emerald-400">{notifSettings.watering_interval_days}</span>
              </label>
              <input type="range" min={1} max={7} value={notifSettings.watering_interval_days}
                onChange={e => setNotifSettings(s => ({ ...s, watering_interval_days: parseInt(e.target.value) }))}
                className="w-full accent-emerald-500"
                data-testid="watering-interval-slider" />
              <div className="flex justify-between text-xs text-emerald-100/30 mt-1">
                <span>1 Tag</span><span>7 Tage</span>
              </div>
            </div>

            {notifMsg && <p className="text-emerald-400 text-sm">{notifMsg}</p>}

            <button type="submit" disabled={savingNotif} className="btn-primary flex items-center gap-2" data-testid="save-notifications-btn">
              <Save size={14} /> {savingNotif ? 'Speichern...' : 'Einstellungen speichern'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
